import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronLeft, ChevronRight, Gamepad2, Mic, MicOff, Send, MessageSquare, ZoomIn, ZoomOut, ArrowUpRight, UserPlus, Bot, Clock, X, RotateCcw, Keyboard, Pencil, Save, ChevronDown, User, Search, HelpCircle, ArrowRight } from 'lucide-react';
import { EmptyState } from './EmptyState';

/* ── Types ─────────────────────────────────────── */

type TaskState = 'pending_review' | 'approved' | 'delegated_waiting' | 'completed';
type CardFocus = 'source' | 'response';
type ViewMode = 'review' | 'refine';
type SubAction = null | 'amend' | 'provide' | 'delegate';
type InputMethod = null | 'voice' | 'text';
interface ActionItem {
  id: string;
  description: string;
  status: 'resolved' | 'needs_input' | 'delegated';
  inputPrompt?: string;
  userInput?: string;
  agentProvided?: string;
}
interface Task {
  id: string;
  state: TaskState;
  priority: 'urgent' | 'high' | 'normal' | 'low';
  source: {
    type: 'email' | 'slack' | 'calendar';
    sender: {
      name: string;
      handle: string;
    };
    receivedAt: string;
    rawContent: string;
  };
  summary: string;
  actionItems: ActionItem[];
  draft: {
    body: string;
    channel: 'email' | 'slack';
  };
}

/*
 * ── Approve logic (computed, not stored) ──
 *
 * canApprove = every item is resolved, delegated, OR has agentProvided
 *   → items with agentProvided get auto-accepted on approve
 *   → only disabled when an item needs_input AND has NO agentProvided
 *
 * allDelegated = every item is delegated (none resolved/needs_input)
 *   → show "Delegated" close button instead of Approve
 *   → task enters 'delegated_waiting' state
 *   → resurfaces when all subtasks complete
 *
 * hasDelegated = at least one item is delegated
 *   → if mixed (some resolved, some delegated), Approve sends
 *     the resolvable parts; delegated parts become subtasks
 */

function computeCanApprove(items: ActionItem[]): boolean {
  return items.every(ai => ai.status === 'resolved' || ai.status === 'delegated' || !!ai.agentProvided);
}
function computeAllDelegated(items: ActionItem[]): boolean {
  return items.length > 0 && items.every(ai => ai.status === 'delegated');
}
function computeHasDelegated(items: ActionItem[]): boolean {
  return items.some(ai => ai.status === 'delegated');
}

/* ── Mock data ─────────────────────────────────── */

const MOCK_TASKS: Task[] = [{
  id: 'task_01',
  state: 'pending_review',
  priority: 'high',
  source: {
    type: 'email',
    sender: {
      name: 'Paul',
      handle: 'paul@partner.co'
    },
    receivedAt: '10:32 AM',
    rawContent: 'Hi Matt,\n\nCould you send over the privacy policy link? I need it for the client proposal going out tomorrow.\n\nAlso, do you have the updated DPA we discussed last week? And can you confirm the data retention period for the new contract?\n\nThanks,\nPaul'
  },
  summary: 'Paul needs: privacy policy link, updated DPA, and data retention confirmation for client proposal (due tomorrow)',
  actionItems: [{
    id: 'ai_01',
    description: 'Privacy policy link',
    status: 'needs_input',
    inputPrompt: 'Confirm the privacy policy URL',
    agentProvided: 'https://contextloops.com/privacy'
  }, {
    id: 'ai_02',
    description: 'Updated DPA document',
    status: 'needs_input',
    inputPrompt: 'Provide or locate the updated DPA'
  }, {
    id: 'ai_03',
    description: 'Data retention period',
    status: 'needs_input',
    inputPrompt: 'Confirm the data retention period',
    agentProvided: '24 months as per our standard terms'
  }],
  draft: {
    body: 'Hi Paul,\n\nHere\'s the privacy policy: {{ai_01}}\n\nRegarding the DPA — {{ai_02}}\n\nOn data retention — {{ai_03}}\n\nLet me know if you need anything else.\n\nBest,\nMatt',
    channel: 'email'
  }
}, {
  id: 'task_02',
  state: 'pending_review',
  priority: 'normal',
  source: {
    type: 'slack',
    sender: {
      name: 'Marta',
      handle: '@marta'
    },
    receivedAt: '11:15 AM',
    rawContent: 'Hey Matt — just checking if you\'re still free for the design review at 3pm today? I\'ve updated the Figma with the new nav patterns.'
  },
  summary: 'Marta confirming 3pm design review — Figma updated with new nav patterns',
  actionItems: [{
    id: 'ai_04',
    description: 'Confirm attendance at 3pm',
    status: 'needs_input',
    inputPrompt: 'Confirm you can attend',
    agentProvided: 'Yes, 3pm works. I\'ll review the Figma beforehand.'
  }],
  draft: {
    body: 'Yep, 3pm works! I\'ll take a look at the Figma beforehand. See you then.',
    channel: 'slack'
  }
}, {
  id: 'task_03',
  state: 'pending_review',
  priority: 'urgent',
  source: {
    type: 'email',
    sender: {
      name: 'NHS Digital',
      handle: 'integration@nhsd.nhs.uk'
    },
    receivedAt: '9:05 AM',
    rawContent: 'Dear Matt,\n\nAs part of the DTAC compliance review, we require the following by end of week:\n\n1. Updated clinical safety case report (DCB0129)\n2. Evidence of penetration testing within the last 12 months\n3. Confirmation of your Cyber Essentials certification status\n\nPlease submit via the Digital Health Portal.\n\nRegards,\nNHS Digital Compliance Team'
  },
  summary: 'NHS Digital needs: clinical safety report, pen test evidence, and Cyber Essentials status — due end of week',
  actionItems: [{
    id: 'ai_05',
    description: 'Clinical safety case report (DCB0129)',
    status: 'needs_input',
    inputPrompt: 'Locate or upload the DCB0129 report'
  }, {
    id: 'ai_06',
    description: 'Penetration testing evidence',
    status: 'needs_input',
    inputPrompt: 'Provide pen test report or certificate'
  }, {
    id: 'ai_07',
    description: 'Cyber Essentials certification',
    status: 'needs_input',
    inputPrompt: 'Confirm certification status',
    agentProvided: 'Current Cyber Essentials certification (valid until March 2027)'
  }],
  draft: {
    body: 'Dear NHS Digital Compliance Team,\n\nThank you for your email. Please find the requested items below:\n\n1. Clinical safety case report — {{ai_05}}\n2. Penetration testing evidence — {{ai_06}}\n3. Cyber Essentials — {{ai_07}}\n\nPlease let me know if you require anything further.\n\nBest regards,\nMatt',
    channel: 'email'
  }
}];

/* ── Hooks ─────────────────────────────────────── */

function useIsMobile(bp = 768) {
  const [m, setM] = useState(window.innerWidth < bp);
  useEffect(() => {
    const f = () => setM(window.innerWidth < bp);
    window.addEventListener('resize', f);
    return () => window.removeEventListener('resize', f);
  }, [bp]);
  return m;
}
interface GPState {
  connected: boolean;
  id: string;
  a: boolean;
  b: boolean;
  x: boolean;
  y: boolean;
  l1: boolean;
  r1: boolean;
  l2: number;
  r2: number;
  leftX: number;
  leftY: number;
  rightX: number;
  rightY: number;
}
function useGamepad(): GPState {
  const [s, setS] = useState<GPState>({
    connected: false,
    id: '',
    a: false,
    b: false,
    x: false,
    y: false,
    l1: false,
    r1: false,
    l2: 0,
    r2: 0,
    leftX: 0,
    leftY: 0,
    rightX: 0,
    rightY: 0
  });
  useEffect(() => {
    let id: number;
    const poll = () => {
      const gp = navigator.getGamepads()[0];
      if (gp) {
        const d = (v: number) => Math.abs(v) < 0.3 ? 0 : v;
        setS({
          connected: true,
          id: gp.id,
          a: gp.buttons[0]?.pressed ?? false,
          b: gp.buttons[1]?.pressed ?? false,
          x: gp.buttons[2]?.pressed ?? false,
          y: gp.buttons[3]?.pressed ?? false,
          l1: gp.buttons[4]?.pressed ?? false,
          r1: gp.buttons[5]?.pressed ?? false,
          l2: gp.buttons[6]?.value ?? 0,
          r2: gp.buttons[7]?.value ?? 0,
          leftX: d(gp.axes[0] ?? 0),
          leftY: d(gp.axes[1] ?? 0),
          rightX: d(gp.axes[2] ?? 0),
          rightY: d(gp.axes[3] ?? 0)
        });
      } else setS(p => p.connected ? {
        ...p,
        connected: false,
        id: ''
      } : p);
      id = requestAnimationFrame(poll);
    };
    id = requestAnimationFrame(poll);
    return () => cancelAnimationFrame(id);
  }, []);
  return s;
}
function useButtonEdge(pressed: boolean, cb: () => void) {
  const w = useRef(false);
  useEffect(() => {
    if (pressed && !w.current) cb();
    w.current = pressed;
  }, [pressed, cb]);
}

/* ── Constants ────────────────────────────────── */

const PRI_CLS: Record<string, string> = {
  urgent: 'bg-red-500/20 text-red-400',
  high: 'bg-amber-500/20 text-amber-400',
  normal: 'bg-[#4a9a9a]/20 text-[#4a9a9a]',
  low: 'bg-[#8a8680]/20 text-[#8a8680]'
};
const SRC_E: Record<string, string> = {
  email: '✉',
  slack: '⌘',
  calendar: '📅'
};

/* ── Help tooltip ─────────────────────────────── */

function HelpTooltip({
  text
}: {
  text: string;
}) {
  const [show, setShow] = useState(false);
  return <span className="relative inline-flex" data-id="element-2009">
      <button onClick={() => setShow(!show)} onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)} className="text-[#8a8680]/30 hover:text-[#8a8680]/60 transition-colors" data-id="element-2010">
        <HelpCircle size={14} data-id="element-2011" />
      </button>
      <AnimatePresence data-id="element-2012">
        {show && <motion.div initial={{
        opacity: 0,
        y: 4,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 4,
        scale: 0.97
      }} transition={{
        duration: 0.15
      }} className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 w-64 bg-[#1c1c22] border border-[#8a8680]/15 rounded-xl shadow-xl p-3 z-50 text-[12px] text-[#e8e4df]/70 leading-relaxed" data-id="element-2013">
            {text}
          </motion.div>}
      </AnimatePresence>
    </span>;
}

/* ── Draft body with clickable placeholders ── */

function DraftBody({
  task,
  textScale,
  onPlaceholderClick
}: {
  task: Task;
  textScale: number;
  onPlaceholderClick?: (id: string) => void;
}) {
  return <div className="leading-relaxed whitespace-pre-wrap" style={{
    fontSize: `${14 * textScale}px`
  }} data-id="element-2014">
      {task.draft.body.split(/(\{\{.*?\}\})/g).map((part, i) => {
      const m = part.match(/\{\{(.+?)\}\}/);
      if (m) {
        const item = task.actionItems.find(a => a.id === m[1]);
        if (!item) return <span key={i} data-id="element-2015">{part}</span>;
        if (item.status === 'resolved' && item.userInput) return <span key={i} className="text-[#4a9a9a] bg-[#4a9a9a]/10 rounded px-1" data-id="element-2016">{item.userInput}</span>;
        if (item.status === 'delegated') return <span key={i} className="text-amber-400/60 bg-amber-500/10 rounded px-1 text-[12px]" data-id="element-2017">⏳ Delegated</span>;
        // Has agent suggestion but not yet accepted
        if (item.agentProvided) return <button key={i} onClick={() => onPlaceholderClick?.(item.id)} className="inline-flex items-center gap-1 text-[#4a9a9a]/70 bg-[#4a9a9a]/10 hover:bg-[#4a9a9a]/20 rounded px-1.5 py-0.5 text-[12px] transition-colors cursor-pointer" data-id="element-2018">✓? {item.agentProvided.length > 40 ? item.agentProvided.slice(0, 40) + '…' : item.agentProvided}</button>;
        return <button key={i} onClick={() => onPlaceholderClick?.(item.id)} className="inline-flex items-center gap-1 text-amber-400/80 bg-amber-500/10 hover:bg-amber-500/20 rounded px-1.5 py-0.5 text-[12px] transition-colors cursor-pointer" data-id="element-2019">⬡ {item.description}</button>;
      }
      return <span key={i} className="text-[#e8e4df]/90" data-id="element-2020">{part}</span>;
    })}
    </div>;
}

/* ── Delegate dropdown ────────────────────── */

function DelegateDropdown({
  onDelegate
}: {
  onDelegate: (type: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const colleagues = ['Paul', 'Marta', 'Tom', 'Sarah'];
  useEffect(() => {
    const fn = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);
  return <div className="flex gap-2" data-id="element-2021">
      <button onClick={() => onDelegate('self')} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#8a8680]/10 text-[#8a8680] text-[12px] font-medium hover:bg-[#8a8680]/20 transition-all" data-id="element-2022"><Clock size={13} data-id="element-2023" /> To self</button>
      <button onClick={() => onDelegate('agent')} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#4a9a9a]/10 text-[#4a9a9a] text-[12px] font-medium hover:bg-[#4a9a9a]/20 transition-all" data-id="element-2024"><Bot size={13} data-id="element-2025" /> To agent</button>
      <div ref={ref} className="relative" data-id="element-2026">
        <button onClick={() => setOpen(!open)} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#8a8680]/10 text-[#8a8680] text-[12px] font-medium hover:bg-[#8a8680]/20 transition-all" data-id="element-2027"><UserPlus size={13} data-id="element-2028" /> To colleague <ChevronDown size={11} className={`transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-2029" /></button>
        <AnimatePresence data-id="element-2030">
          {open && <motion.div initial={{
          opacity: 0,
          y: -4
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -4
        }} transition={{
          duration: 0.15
        }} className="absolute bottom-full mb-2 right-0 w-48 bg-[#1c1c22] border border-[#8a8680]/15 rounded-xl shadow-xl overflow-hidden z-40" data-id="element-2031">
              {colleagues.map(c => <button key={c} onClick={() => {
            onDelegate(`colleague:${c}`);
            setOpen(false);
          }} className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-[#e8e4df]/80 hover:bg-[#4a9a9a]/10 transition-colors text-left" data-id="element-2032"><User size={12} className="text-[#8a8680]/40" data-id="element-2033" /> {c}</button>)}
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}

/* ── Subtask panel ─────────────────────── */

function SubtaskPanel({
  item,
  subAction,
  setSubAction,
  inputMethod,
  setInputMethod,
  inputText,
  setInputText,
  listening,
  setListening,
  onAccept,
  onSaveAmend,
  onDelegate
}: {
  item: ActionItem;
  subAction: SubAction;
  setSubAction: (a: SubAction) => void;
  inputMethod: InputMethod;
  setInputMethod: (m: InputMethod) => void;
  inputText: string;
  setInputText: (t: string) => void;
  listening: boolean;
  setListening: (l: boolean) => void;
  onAccept: () => void;
  onSaveAmend: (text: string) => void;
  onDelegate: (type: string, instruction: string) => void;
}) {
  const hasAgent = !!item.agentProvided;
  if (item.status === 'resolved' && item.userInput) {
    return <div className="bg-[#4a9a9a]/10 rounded-xl p-3 border border-[#4a9a9a]/20" data-id="element-2034"><p className="text-[10px] text-[#4a9a9a] uppercase tracking-wider font-medium flex items-center gap-1 mb-1" data-id="element-2035"><Check size={10} data-id="element-2036" /> Resolved</p><p className="text-[#e8e4df] text-[14px]" data-id="element-2037">{item.userInput}</p></div>;
  }
  if (item.status === 'delegated') {
    return <div className="bg-amber-500/10 rounded-xl p-3 border border-amber-500/20" data-id="element-2038"><p className="text-[10px] text-amber-400 uppercase tracking-wider font-medium flex items-center gap-1 mb-1" data-id="element-2039"><Clock size={10} data-id="element-2040" /> Delegated</p><p className="text-[#e8e4df]/70 text-[14px]" data-id="element-2041">{item.userInput}</p></div>;
  }

  // Default buttons
  if (!subAction) {
    return <div className="flex flex-col gap-4" data-id="element-2042">
        {item.inputPrompt && <p className="text-[#8a8680]/50 text-[13px]" data-id="element-2043">{item.inputPrompt}</p>}
        {hasAgent && <div className="bg-[#4a9a9a]/8 border border-[#4a9a9a]/15 rounded-xl p-3" data-id="element-2044">
            <p className="text-[10px] text-[#4a9a9a]/60 uppercase tracking-wider font-medium flex items-center gap-1 mb-1.5" data-id="element-2045"><Bot size={10} data-id="element-2046" /> Agent suggestion</p>
            <p className="text-[#e8e4df]/80 text-[14px] leading-relaxed" data-id="element-2047">{item.agentProvided}</p>
          </div>}
        <div className="flex gap-2" data-id="element-2048">
          {hasAgent ? <>
              <button onClick={onAccept} className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-[#4a9a9a]/15 text-[#4a9a9a] text-[13px] font-medium hover:bg-[#4a9a9a]/25 transition-all" data-id="element-2049"><Check size={14} data-id="element-2050" /> Accept</button>
              <button onClick={() => setSubAction('amend')} className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-[#8a8680]/10 text-[#8a8680] text-[13px] font-medium hover:bg-[#8a8680]/20 transition-all" data-id="element-2051"><Pencil size={14} data-id="element-2052" /> Amend</button>
            </> : <button onClick={() => setSubAction('provide')} className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-[#4a9a9a]/15 text-[#4a9a9a] text-[13px] font-medium hover:bg-[#4a9a9a]/25 transition-all" data-id="element-2053"><Pencil size={14} data-id="element-2054" /> Provide</button>}
          <button onClick={() => setSubAction('delegate')} className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-[#8a8680]/10 text-[#8a8680] text-[13px] font-medium hover:bg-[#8a8680]/20 transition-all" data-id="element-2055"><UserPlus size={14} data-id="element-2056" /> Delegate</button>
        </div>
      </div>;
  }

  // Amend
  if (subAction === 'amend') {
    return <div className="flex flex-col gap-3" data-id="element-2057">
        <div className="bg-[#4a9a9a]/8 border border-[#4a9a9a]/15 rounded-xl p-3" data-id="element-2058">
          <p className="text-[10px] text-[#4a9a9a]/60 uppercase tracking-wider font-medium flex items-center gap-1 mb-1.5" data-id="element-2059"><Bot size={10} data-id="element-2060" /> Current suggestion</p>
          <p className="text-[#e8e4df]/80 text-[14px] leading-relaxed" data-id="element-2061">{item.agentProvided}</p>
        </div>
        {!inputMethod ? <div className="flex gap-3" data-id="element-2062">
            <button onClick={() => setInputMethod('text')} className="flex-1 flex flex-col items-center justify-center gap-2 py-5 rounded-xl bg-[#141418] text-[#8a8680] hover:text-[#e8e4df] transition-colors" data-id="element-2063"><Pencil size={20} data-id="element-2064" /><span className="text-[11px] font-medium" data-id="element-2065">Type amendment</span></button>
            <button onClick={() => {
          setInputMethod('voice');
          setListening(true);
          setTimeout(() => {
            setInputText('Actually make it 36 months, we updated the terms last quarter');
            setListening(false);
          }, 1500);
        }} className="flex-1 flex flex-col items-center justify-center gap-2 py-5 rounded-xl bg-[#141418] text-[#4a9a9a] hover:text-[#5bb5b5] transition-colors" data-id="element-2066"><Mic size={20} data-id="element-2067" /><span className="text-[11px] font-medium" data-id="element-2068">Speak amendment</span></button>
          </div> : <div className="flex flex-col gap-2" data-id="element-2069">
            {inputMethod === 'voice' ? <div className="flex items-center gap-3 bg-[#141418] rounded-xl px-4 py-3" data-id="element-2070">
                <button onClick={() => {
            setListening(!listening);
            if (!listening) setTimeout(() => {
              setInputText('Actually make it 36 months, we updated the terms last quarter');
              setListening(false);
            }, 1500);
          }} className={`p-2.5 rounded-full transition-all ${listening ? 'bg-red-500/20 text-red-400 animate-pulse' : 'bg-[#4a9a9a]/15 text-[#4a9a9a]'}`} data-id="element-2071">{listening ? <MicOff size={15} data-id="element-2072" /> : <Mic size={15} data-id="element-2073" />}</button>
                <p className="flex-1 text-[13px] text-[#e8e4df]/70 min-h-[20px]" data-id="element-2074">{inputText || (listening ? 'Listening…' : 'Tap mic')}</p>
                <button onClick={() => setInputMethod('text')} className="text-[#8a8680]/40 p-1" data-id="element-2075"><Keyboard size={14} data-id="element-2076" /></button>
              </div> : <div className="relative" data-id="element-2077"><textarea autoFocus value={inputText} onChange={e => setInputText(e.target.value)} placeholder="Describe what to change…" className="w-full bg-[#141418] rounded-xl p-3 pr-10 text-[#e8e4df] text-[13px] resize-none outline-none min-h-[60px] placeholder:text-[#8a8680]/30" data-id="element-2078" /><button onClick={() => setInputMethod('voice')} className="absolute right-3 top-3 text-[#8a8680]/30 hover:text-[#4a9a9a] transition-colors" data-id="element-2079"><Mic size={14} data-id="element-2080" /></button></div>}
            <button onClick={() => onSaveAmend(inputText)} disabled={!inputText} className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#4a9a9a] text-[#141418] text-[13px] font-medium disabled:opacity-30 transition-all" data-id="element-2081"><Save size={13} data-id="element-2082" /> Save</button>
          </div>}
        <button onClick={() => {
        setSubAction(null);
        setInputMethod(null);
        setInputText('');
      }} className="text-[#8a8680]/40 text-[12px] hover:text-[#8a8680] transition-colors self-start" data-id="element-2083">← Back</button>
      </div>;
  }

  // Provide
  if (subAction === 'provide') {
    return <div className="flex flex-col gap-3" data-id="element-2084">
        <p className="text-[#8a8680]/50 text-[13px]" data-id="element-2085">{item.inputPrompt || item.description}</p>
        {!inputMethod ? <div className="flex gap-3" data-id="element-2086">
            <button onClick={() => setInputMethod('text')} className="flex-1 flex flex-col items-center justify-center gap-2 py-5 rounded-xl bg-[#141418] text-[#8a8680] hover:text-[#e8e4df] transition-colors" data-id="element-2087"><Pencil size={20} data-id="element-2088" /><span className="text-[11px] font-medium" data-id="element-2089">Type</span></button>
            <button onClick={() => {
          setInputMethod('voice');
          setListening(true);
          setTimeout(() => {
            setInputText('The updated DPA is in shared Drive under Legal > Contracts');
            setListening(false);
          }, 1500);
        }} className="flex-1 flex flex-col items-center justify-center gap-2 py-5 rounded-xl bg-[#141418] text-[#4a9a9a] hover:text-[#5bb5b5] transition-colors" data-id="element-2090"><Mic size={20} data-id="element-2091" /><span className="text-[11px] font-medium" data-id="element-2092">Speak</span></button>
          </div> : <div className="flex flex-col gap-2" data-id="element-2093">
            {inputMethod === 'voice' ? <div className="flex items-center gap-3 bg-[#141418] rounded-xl px-4 py-3" data-id="element-2094">
                <button onClick={() => {
            setListening(!listening);
            if (!listening) setTimeout(() => {
              setInputText('The updated DPA is in shared Drive under Legal > Contracts');
              setListening(false);
            }, 1500);
          }} className={`p-2.5 rounded-full transition-all ${listening ? 'bg-red-500/20 text-red-400 animate-pulse' : 'bg-[#4a9a9a]/15 text-[#4a9a9a]'}`} data-id="element-2095">{listening ? <MicOff size={15} data-id="element-2096" /> : <Mic size={15} data-id="element-2097" />}</button>
                <p className="flex-1 text-[13px] text-[#e8e4df]/70 min-h-[20px]" data-id="element-2098">{inputText || (listening ? 'Listening…' : 'Tap mic')}</p>
                <button onClick={() => setInputMethod('text')} className="text-[#8a8680]/40 p-1" data-id="element-2099"><Keyboard size={14} data-id="element-2100" /></button>
              </div> : <div className="relative" data-id="element-2101"><textarea autoFocus value={inputText} onChange={e => setInputText(e.target.value)} placeholder="Type your response…" className="w-full bg-[#141418] rounded-xl p-3 pr-10 text-[#e8e4df] text-[13px] resize-none outline-none min-h-[60px] placeholder:text-[#8a8680]/30" data-id="element-2102" /><button onClick={() => setInputMethod('voice')} className="absolute right-3 top-3 text-[#8a8680]/30 hover:text-[#4a9a9a] transition-colors" data-id="element-2103"><Mic size={14} data-id="element-2104" /></button></div>}
            <button onClick={() => onSaveAmend(inputText)} disabled={!inputText} className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#4a9a9a] text-[#141418] text-[13px] font-medium disabled:opacity-30 transition-all" data-id="element-2105"><Save size={13} data-id="element-2106" /> Save</button>
          </div>}
        <button onClick={() => {
        setSubAction(null);
        setInputMethod(null);
        setInputText('');
      }} className="text-[#8a8680]/40 text-[12px] hover:text-[#8a8680] transition-colors self-start" data-id="element-2107">← Back</button>
      </div>;
  }

  // Delegate
  if (subAction === 'delegate') {
    return <div className="flex flex-col gap-3" data-id="element-2108">
        <div className="bg-[#8a8680]/8 border border-[#8a8680]/12 rounded-xl p-3" data-id="element-2109"><p className="text-[10px] text-[#8a8680]/50 uppercase tracking-wider font-medium mb-1" data-id="element-2110">Task</p><p className="text-[#e8e4df]/80 text-[14px]" data-id="element-2111">{item.description}</p></div>
        <div className="relative" data-id="element-2112"><textarea value={inputText} onChange={e => setInputText(e.target.value)} placeholder="Further instructions (optional)…" className="w-full bg-[#141418] rounded-xl p-3 pr-10 text-[#e8e4df] text-[13px] resize-none outline-none min-h-[50px] placeholder:text-[#8a8680]/30" data-id="element-2113" /><button onClick={() => {
          setListening(!listening);
          if (!listening) setTimeout(() => {
            setInputText('I need to do this when I\'m at my desk');
            setListening(false);
          }, 1200);
        }} className={`absolute right-3 top-3 transition-colors ${listening ? 'text-red-400 animate-pulse' : 'text-[#8a8680]/30 hover:text-[#4a9a9a]'}`} data-id="element-2114">{listening ? <MicOff size={14} data-id="element-2115" /> : <Mic size={14} data-id="element-2116" />}</button></div>
        <DelegateDropdown onDelegate={type => onDelegate(type, inputText)} data-id="element-2117" />
        <button onClick={() => {
        setSubAction(null);
        setInputMethod(null);
        setInputText('');
        setListening(false);
      }} className="text-[#8a8680]/40 text-[12px] hover:text-[#8a8680] transition-colors self-start" data-id="element-2118">← Back</button>
      </div>;
  }
  return null;
}

/* ── Action bar component (shared) ────────────── */

const DELEGATED_HELP = 'When a task is fully delegated, clicking "Delegated" closes the task and moves it to a waiting state. The agent tracks all delegated subtasks (sent to self, colleagues, or agent searches). When every subtask is completed, the task automatically resurfaces in your review queue with the resolved content filled in, ready for final approval and sending.';
function ActionBar({
  task,
  mode,
  setMode,
  setActiveTabId,
  setSubAction,
  setInputMethod,
  handleApprove,
  handleDelegatedClose,
  gamepad,
  isMobile
}: any) {
  const canApprove = useMemo(() => computeCanApprove(task.actionItems), [task.actionItems]);
  const allDelegated = useMemo(() => computeAllDelegated(task.actionItems), [task.actionItems]);
  const approved = task.state === 'approved';
  const delegatedWaiting = task.state === 'delegated_waiting';
  if (approved) return <motion.div key="ok" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="flex items-center justify-center gap-2 py-3 text-[#4a9a9a]" data-id="element-2119"><Check size={16} data-id="element-2120" /> Sent</motion.div>;
  if (delegatedWaiting) return <motion.div key="dw" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="flex items-center justify-center gap-2 py-3 text-amber-400" data-id="element-2121"><Clock size={16} data-id="element-2122" /> Waiting on delegated tasks</motion.div>;
  if (mode === 'review') {
    return <motion.div key="rev" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="flex items-center justify-center gap-3" data-id="element-2123">
        {allDelegated ? <div className="flex items-center gap-2" data-id="element-2124">
            <button onClick={handleDelegatedClose} className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-amber-500/20 text-amber-400 text-sm font-medium hover:bg-amber-500/30 transition-all" data-id="element-2125">
              <Clock size={14} data-id="element-2126" /> Delegated
            </button>
            <HelpTooltip text={DELEGATED_HELP} data-id="element-2127" />
          </div> : <button onClick={handleApprove} disabled={!canApprove} className={`flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-medium transition-all ${canApprove ? 'bg-[#4a9a9a] text-[#141418] hover:bg-[#5bb5b5]' : 'bg-[#8a8680]/10 text-[#8a8680]/30 cursor-not-allowed'}`} data-id="element-2128">
            <Check size={14} data-id="element-2129" /> Approve {!isMobile && <span className="text-[9px] opacity-50 ml-1" data-id="element-2130">A</span>}
          </button>}
        <button onClick={() => {
        setMode('refine');
        setActiveTabId(task.actionItems[0]?.id);
        setSubAction(null);
        setInputMethod(null);
      }} className="flex items-center gap-2 px-6 py-2.5 rounded-full text-[#8a8680] text-sm font-medium hover:text-[#e8e4df] transition-colors" data-id="element-2131">
          <MessageSquare size={14} data-id="element-2132" /> Refine {!isMobile && <span className="text-[9px] opacity-40 ml-1" data-id="element-2133">X</span>}
        </button>
      </motion.div>;
  }

  // Refine mode
  return <motion.div key="ref" initial={{
    opacity: 0,
    y: 6
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0
  }} className="flex items-center justify-center gap-3" data-id="element-2134">
      <button onClick={() => {
      setMode('review');
      setSubAction(null);
      setInputMethod(null);
    }} className="flex items-center gap-2 px-5 py-2.5 rounded-full text-[#8a8680] text-sm hover:text-[#e8e4df] transition-colors" data-id="element-2135"><RotateCcw size={13} data-id="element-2136" /> Back {!isMobile && <span className="text-[9px] opacity-40 ml-1" data-id="element-2137">B</span>}</button>
      {allDelegated ? <div className="flex items-center gap-2" data-id="element-2138">
          <button onClick={handleDelegatedClose} className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-amber-500/20 text-amber-400 text-sm font-medium hover:bg-amber-500/30 transition-all" data-id="element-2139"><Clock size={14} data-id="element-2140" /> Delegated</button>
          <HelpTooltip text={DELEGATED_HELP} data-id="element-2141" />
        </div> : canApprove ? <button onClick={handleApprove} className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#4a9a9a] text-[#141418] text-sm font-medium hover:bg-[#5bb5b5] transition-colors" data-id="element-2142"><Check size={14} data-id="element-2143" /> Approve</button> : null}
    </motion.div>;
}

/* ═══ DESKTOP LAYOUT ═══ */

function DesktopLayout({
  task,
  tasks,
  currentIndex,
  mode,
  focus,
  zoomed,
  textScale,
  activeTabId,
  subAction,
  inputMethod,
  inputText,
  listening,
  setFocus,
  setZoomed,
  setMode,
  setActiveTabId,
  setSubAction,
  setInputMethod,
  setInputText,
  setListening,
  handleApprove,
  handleAccept,
  handleSaveAmend,
  handleDelegateConfirm,
  handleDelegatedClose,
  goNext,
  goPrev,
  gamepad
}: any) {
  const openRefineTab = (itemId: string) => {
    setMode('refine');
    setActiveTabId(itemId);
    setSubAction(null);
    setInputMethod(null);
    setInputText('');
  };
  return <div className="min-h-[calc(100vh-56px)] w-full flex flex-col relative" data-id="element-2144">
      <div className="absolute top-4 right-4 flex items-center gap-2 text-xs z-20" data-id="element-2145">
        <Gamepad2 size={14} className={gamepad.connected ? 'text-[#4a9a9a]' : 'text-[#8a8680]/30'} data-id="element-2146" />
        <span className={gamepad.connected ? 'text-[#4a9a9a]' : 'text-[#8a8680]/30'} data-id="element-2147">{gamepad.connected ? gamepad.id.split('(')[0]?.trim().slice(0, 30) || 'Connected' : 'No controller'}</span>
      </div>

      <div className="flex items-center justify-between px-6 pt-4 pb-2" data-id="element-2148">
        <div className="flex items-center gap-3" data-id="element-2149">
          <span className={`text-[10px] uppercase tracking-[0.1em] font-medium px-2 py-0.5 rounded-full ${PRI_CLS[task.priority]}`} data-id="element-2150">{task.priority}</span>
          <span className="text-[11px] text-[#8a8680]/40" data-id="element-2151">{SRC_E[task.source.type]} {task.source.sender.name} · {task.source.receivedAt}</span>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-[#8a8680]/40" data-id="element-2152">
          <span data-id="element-2153">{currentIndex + 1} / {tasks.length}</span>
          <button onClick={goPrev} disabled={currentIndex === 0} className="disabled:opacity-20 hover:text-[#e8e4df] transition-colors" data-id="element-2154"><ChevronLeft size={14} data-id="element-2155" /></button>
          <button onClick={goNext} disabled={currentIndex === tasks.length - 1} className="disabled:opacity-20 hover:text-[#e8e4df] transition-colors" data-id="element-2156"><ChevronRight size={14} data-id="element-2157" /></button>
        </div>
      </div>

      <div className="flex-1 flex gap-3 px-4 pb-2 overflow-hidden min-h-0" data-id="element-2158">
        <AnimatePresence mode="wait" data-id="element-2159">
          <motion.div key={task.id} initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -12
        }} transition={{
          duration: 0.3
        }} className="flex gap-3 flex-1 min-h-0" data-id="element-2160">
            <motion.div animate={{
            flex: mode === 'refine' ? 0.7 : zoomed && focus === 'source' ? 2 : 1
          }} transition={{
            duration: 0.3
          }} onClick={() => {
            setFocus('source');
            if (focus === 'source' && mode !== 'refine') setZoomed(!zoomed);
          }} className={`rounded-2xl bg-[#1c1c22] p-5 overflow-y-auto cursor-pointer flex flex-col gap-3 ${focus === 'source' ? 'ring-1 ring-[#4a9a9a]/40' : 'ring-1 ring-transparent hover:ring-[#8a8680]/15'}`} data-id="element-2161">
              <span className="text-[10px] uppercase tracking-[0.1em] text-[#8a8680]/50 font-medium" data-id="element-2162">Incoming · {task.draft.channel}</span>
              {(!zoomed || focus !== 'source') && mode !== 'refine' ? <p className="text-[#e8e4df] leading-relaxed" style={{
              fontSize: `${14 * textScale}px`
            }} data-id="element-2163">{task.summary}</p> : <p className="text-[#e8e4df] leading-relaxed whitespace-pre-wrap" style={{
              fontSize: `${14 * textScale}px`
            }} data-id="element-2164">{task.source.rawContent}</p>}
            </motion.div>

            <motion.div animate={{
            flex: mode === 'refine' ? 0.7 : zoomed && focus === 'response' ? 2 : 1
          }} transition={{
            duration: 0.3
          }} onClick={() => {
            setFocus('response');
            if (focus === 'response' && mode !== 'refine') setZoomed(!zoomed);
          }} className={`rounded-2xl bg-[#1c1c22] p-5 overflow-y-auto cursor-pointer flex flex-col gap-3 border-l-[3px] ${computeCanApprove(task.actionItems) ? 'border-l-[#4a9a9a]/50' : 'border-l-amber-500/40'} ${focus === 'response' ? 'ring-1 ring-[#4a9a9a]/40' : 'ring-1 ring-transparent hover:ring-[#8a8680]/15'}`} data-id="element-2165">
              <div className="flex items-center gap-2" data-id="element-2166">
                <span className="text-[10px] uppercase tracking-[0.1em] text-[#8a8680]/50 font-medium" data-id="element-2167">Draft response</span>
                {!computeCanApprove(task.actionItems) && <span className="text-[10px] text-amber-400/60" data-id="element-2168">· items need review</span>}
              </div>
              <DraftBody task={task} textScale={textScale} onPlaceholderClick={openRefineTab} data-id="element-2169" />
            </motion.div>

            <AnimatePresence data-id="element-2170">
              {mode === 'refine' && <motion.div initial={{
              opacity: 0,
              width: 0
            }} animate={{
              opacity: 1,
              flex: 1.3
            }} exit={{
              opacity: 0,
              width: 0
            }} transition={{
              duration: 0.3
            }} className="rounded-2xl bg-[#1c1c22] flex flex-col overflow-hidden min-w-0" data-id="element-2171">
                  <div className="flex border-b border-[#8a8680]/10 overflow-x-auto shrink-0" data-id="element-2172">
                    {task.actionItems.map((item: ActionItem, idx: number) => <button key={item.id} onClick={() => {
                  setActiveTabId(item.id);
                  setSubAction(null);
                  setInputMethod(null);
                  setInputText('');
                }} className={`flex-shrink-0 px-4 py-3 text-[12px] font-medium transition-all border-b-2 ${activeTabId === item.id ? 'border-b-[#4a9a9a] text-[#e8e4df]' : 'border-b-transparent text-[#8a8680]/50 hover:text-[#8a8680]'}`} data-id="element-2173">
                        <span className="flex items-center gap-1.5" data-id="element-2174">
                          {item.status === 'resolved' && <Check size={10} className="text-[#4a9a9a]" data-id="element-2175" />}
                          {item.status === 'delegated' && <Clock size={10} className="text-amber-400" data-id="element-2176" />}
                          {idx + 1}. {item.description.length > 18 ? item.description.slice(0, 18) + '…' : item.description}
                        </span>
                      </button>)}
                  </div>
                  {task.actionItems.map((item: ActionItem) => item.id !== activeTabId ? null : <div key={item.id} className="flex-1 flex flex-col p-5 overflow-y-auto" data-id="element-2177">
                      <h4 className="text-[#e8e4df] text-[15px] font-medium mb-3" data-id="element-2178">{item.description}</h4>
                      <SubtaskPanel item={item} subAction={subAction} setSubAction={setSubAction} inputMethod={inputMethod} setInputMethod={setInputMethod} inputText={inputText} setInputText={setInputText} listening={listening} setListening={setListening} onAccept={() => handleAccept(item.id)} onSaveAmend={(text: string) => handleSaveAmend(item.id, text)} onDelegate={(type: string, instruction: string) => handleDelegateConfirm(item.id, type, instruction)} data-id="element-2179" />
                    </div>)}
                </motion.div>}
            </AnimatePresence>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="px-4 pb-4 pt-1" data-id="element-2180">
        <AnimatePresence mode="wait" data-id="element-2181">
          <ActionBar task={task} mode={mode} setMode={setMode} setActiveTabId={setActiveTabId} setSubAction={setSubAction} setInputMethod={setInputMethod} handleApprove={handleApprove} handleDelegatedClose={handleDelegatedClose} gamepad={gamepad} isMobile={false} data-id="element-2182" />
        </AnimatePresence>
        {gamepad.connected && mode === 'review' && <div className="flex items-center justify-center gap-5 mt-3 text-[10px] text-[#8a8680]/25" data-id="element-2183"><span data-id="element-2184">🕹 Focus</span><span data-id="element-2185">L1/R1 Text size</span><span data-id="element-2186">L2/R2 Prev/Next</span></div>}
      </div>
    </div>;
}

/* ═══ MOBILE LAYOUT ═══ */

function MobileLayout({
  task,
  tasks,
  currentIndex,
  mode,
  setMode,
  subAction,
  setSubAction,
  inputMethod,
  setInputMethod,
  inputText,
  setInputText,
  listening,
  setListening,
  handleApprove,
  handleAccept,
  handleSaveAmend,
  handleDelegateConfirm,
  handleDelegatedClose,
  goNext,
  goPrev,
  activeTabId,
  setActiveTabId
}: any) {
  const approved = task.state === 'approved';
  const delegatedWaiting = task.state === 'delegated_waiting';
  const canApprove = useMemo(() => computeCanApprove(task.actionItems), [task.actionItems]);
  const allDelegated = useMemo(() => computeAllDelegated(task.actionItems), [task.actionItems]);
  const openRefine = (itemId?: string) => {
    setMode('refine');
    setActiveTabId(itemId || task.actionItems[0]?.id);
    setSubAction(null);
    setInputMethod(null);
    setInputText('');
  };
  return <div className="min-h-[calc(100vh-56px)] w-full flex flex-col bg-[#141418] relative" data-id="element-2187">
      <div className="flex items-center justify-between px-5 pt-4 pb-2 shrink-0" data-id="element-2188">
        <div className="flex items-center gap-2" data-id="element-2189">
          <span className={`text-[10px] uppercase tracking-[0.1em] font-medium px-2 py-0.5 rounded-full ${PRI_CLS[task.priority]}`} data-id="element-2190">{task.priority}</span>
          <span className="text-[11px] text-[#8a8680]/40" data-id="element-2191">{SRC_E[task.source.type]} {task.source.sender.name}</span>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-[#8a8680]/40" data-id="element-2192">
          <button onClick={goPrev} disabled={currentIndex === 0} className="disabled:opacity-20" data-id="element-2193"><ChevronLeft size={16} data-id="element-2194" /></button>
          <span data-id="element-2195">{currentIndex + 1}/{tasks.length}</span>
          <button onClick={goNext} disabled={currentIndex === tasks.length - 1} className="disabled:opacity-20" data-id="element-2196"><ChevronRight size={16} data-id="element-2197" /></button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 pb-32" data-id="element-2198">
        <div className="py-5" data-id="element-2199">
          <span className="text-[10px] uppercase tracking-[0.12em] text-[#8a8680]/40 font-medium" data-id="element-2200">Incoming · {task.draft.channel}</span>
          <p className="text-[#e8e4df] text-[15px] leading-[1.7] mt-3 whitespace-pre-wrap" data-id="element-2201">{task.source.rawContent}</p>
          <p className="text-[#8a8680]/30 text-[11px] mt-3" data-id="element-2202">{task.source.sender.handle} · {task.source.receivedAt}</p>
        </div>
        <div className="h-px bg-[#8a8680]/10" data-id="element-2203" />
        <div className="py-5" data-id="element-2204">
          <div className="flex items-center gap-2 mb-3" data-id="element-2205">
            <span className="text-[10px] uppercase tracking-[0.12em] text-[#8a8680]/40 font-medium" data-id="element-2206">Suggested reply</span>
            {!canApprove && <span className="text-[10px] text-amber-400/50" data-id="element-2207">· items need review</span>}
          </div>
          <DraftBody task={task} textScale={1} onPlaceholderClick={openRefine} data-id="element-2208" />
        </div>
      </div>

      {/* Fixed bottom */}
      {!approved && !delegatedWaiting && mode === 'review' && <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#141418] via-[#141418] to-transparent pt-8 pb-5 px-5" data-id="element-2209">
          <div className="flex gap-2" data-id="element-2210">
            {allDelegated ? <div className="flex-1 flex items-center justify-center gap-2" data-id="element-2211">
                <button onClick={handleDelegatedClose} className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl bg-amber-500/20 text-amber-400 text-[14px] font-medium" data-id="element-2212"><Clock size={15} data-id="element-2213" /> Delegated</button>
                <HelpTooltip text={DELEGATED_HELP} data-id="element-2214" />
              </div> : <button onClick={handleApprove} disabled={!canApprove} className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl text-[14px] font-medium transition-all ${canApprove ? 'bg-[#4a9a9a] text-[#141418]' : 'bg-[#8a8680]/10 text-[#8a8680]/30'}`} data-id="element-2215"><Check size={15} data-id="element-2216" /> Approve</button>}
            <button onClick={() => openRefine()} className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl bg-[#1c1c22] text-[#8a8680] text-[14px] font-medium" data-id="element-2217"><MessageSquare size={15} data-id="element-2218" /> Refine</button>
          </div>
        </div>}
      {approved && <div className="absolute bottom-0 left-0 right-0 bg-[#141418] pb-5 pt-3 flex items-center justify-center gap-2 text-[#4a9a9a]" data-id="element-2219"><Check size={16} data-id="element-2220" /> Sent</div>}
      {delegatedWaiting && <div className="absolute bottom-0 left-0 right-0 bg-[#141418] pb-5 pt-3 flex items-center justify-center gap-2 text-amber-400" data-id="element-2221"><Clock size={16} data-id="element-2222" /> Waiting on delegated tasks</div>}

      {/* Modal: subtask list */}
      <AnimatePresence data-id="element-2223">
        {mode === 'refine' && !subAction && <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="absolute bottom-0 left-0 right-0 bg-[#1c1c22] rounded-t-3xl z-20 max-h-[70vh] flex flex-col" data-id="element-2224">
            <div className="flex items-center justify-between px-5 pt-4 pb-2 shrink-0" data-id="element-2225">
              <h3 className="text-[#e8e4df] text-[15px] font-medium" data-id="element-2226">Action items</h3>
              <button onClick={() => setMode('review')} className="text-[#8a8680]/50 p-1" data-id="element-2227"><X size={18} data-id="element-2228" /></button>
            </div>
            <div className="h-px bg-[#8a8680]/10" data-id="element-2229" />
            <div className="flex-1 overflow-y-auto px-5 py-3" data-id="element-2230">
              {task.actionItems.map((item: ActionItem, idx: number) => {
            const hasAgent = !!item.agentProvided;
            return <div key={item.id} className="py-4 border-b border-[#8a8680]/8 last:border-b-0" data-id="element-2231">
                    <div className="flex items-start justify-between gap-3" data-id="element-2232">
                      <div className="flex-1 min-w-0" data-id="element-2233">
                        <div className="flex items-center gap-2 mb-1" data-id="element-2234">
                          {item.status === 'resolved' && <Check size={11} className="text-[#4a9a9a]" data-id="element-2235" />}
                          {item.status === 'delegated' && <Clock size={11} className="text-amber-400" data-id="element-2236" />}
                          <span className="text-[#e8e4df] text-[14px] font-medium" data-id="element-2237">{item.description}</span>
                        </div>
                        {hasAgent && item.status !== 'resolved' && item.status !== 'delegated' && <p className="text-[#4a9a9a]/50 text-[12px] mt-1 flex items-center gap-1" data-id="element-2238"><Bot size={10} data-id="element-2239" /> {item.agentProvided!.length > 50 ? item.agentProvided!.slice(0, 50) + '…' : item.agentProvided}</p>}
                        {item.status === 'resolved' && item.userInput && <p className="text-[#4a9a9a] text-[12px] mt-1" data-id="element-2240">✓ {item.userInput}</p>}
                        {item.status === 'delegated' && item.userInput && <p className="text-amber-400/60 text-[12px] mt-1" data-id="element-2241">⏳ {item.userInput}</p>}
                      </div>
                      {item.status !== 'resolved' && item.status !== 'delegated' && <div className="flex gap-1.5 shrink-0" data-id="element-2242">
                          {hasAgent ? <>
                              <button onClick={() => handleAccept(item.id)} className="flex items-center gap-1.5 text-[12px] text-[#4a9a9a] bg-[#4a9a9a]/10 rounded-xl px-3 py-2" data-id="element-2243"><Check size={12} data-id="element-2244" /> Accept</button>
                              <button onClick={() => {
                      setActiveTabId(item.id);
                      setSubAction('amend');
                      setInputMethod(null);
                      setInputText('');
                    }} className="flex items-center gap-1.5 text-[12px] text-[#8a8680] bg-[#8a8680]/10 rounded-xl px-3 py-2" data-id="element-2245"><Pencil size={12} data-id="element-2246" /> Amend</button>
                            </> : <button onClick={() => {
                    setActiveTabId(item.id);
                    setSubAction('provide');
                    setInputMethod(null);
                    setInputText('');
                  }} className="flex items-center gap-1.5 text-[12px] text-[#4a9a9a] bg-[#4a9a9a]/10 rounded-xl px-3 py-2" data-id="element-2247"><Pencil size={12} data-id="element-2248" /> Provide</button>}
                          <button onClick={() => {
                    setActiveTabId(item.id);
                    setSubAction('delegate');
                    setInputMethod(null);
                    setInputText('');
                  }} className="flex items-center gap-1.5 text-[12px] text-[#8a8680] bg-[#8a8680]/10 rounded-xl px-3 py-2" data-id="element-2249"><UserPlus size={12} data-id="element-2250" /></button>
                        </div>}
                    </div>
                  </div>;
          })}
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Modal: sub-action */}
      <AnimatePresence data-id="element-2251">
        {mode === 'refine' && subAction && <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="absolute bottom-0 left-0 right-0 bg-[#1c1c22] rounded-t-3xl z-30 max-h-[80vh] flex flex-col" data-id="element-2252">
            <div className="flex items-center justify-between px-5 pt-4 pb-2" data-id="element-2253">
              <h3 className="text-[#e8e4df] text-[14px] font-medium" data-id="element-2254">{task.actionItems.find((a: ActionItem) => a.id === activeTabId)?.description}</h3>
              <button onClick={() => {
            setSubAction(null);
            setInputMethod(null);
            setListening(false);
          }} className="text-[#8a8680]/50 p-1" data-id="element-2255"><X size={18} data-id="element-2256" /></button>
            </div>
            <div className="h-px bg-[#8a8680]/10" data-id="element-2257" />
            <div className="flex-1 overflow-y-auto px-5 py-4" data-id="element-2258">
              {task.actionItems.filter((a: ActionItem) => a.id === activeTabId).map((item: ActionItem) => <SubtaskPanel key={item.id} item={item} subAction={subAction} setSubAction={(a: SubAction) => {
            if (!a) {
              setSubAction(null);
              setInputMethod(null);
            } else setSubAction(a);
          }} inputMethod={inputMethod} setInputMethod={setInputMethod} inputText={inputText} setInputText={setInputText} listening={listening} setListening={setListening} onAccept={() => handleAccept(item.id)} onSaveAmend={(text: string) => {
            handleSaveAmend(item.id, text);
            setSubAction(null);
          }} onDelegate={(type: string, instruction: string) => {
            handleDelegateConfirm(item.id, type, instruction);
            setSubAction(null);
          }} data-id="element-2259" />)}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}

/* ═══ MAIN EXPORT ═══ */

export function TaskReview() {
  const isMobile = useIsMobile();
  const gamepad = useGamepad();
  const [tasks, setTasks] = useState<Task[]>(MOCK_TASKS);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [focus, setFocus] = useState<CardFocus>('source');
  const [mode, setMode] = useState<ViewMode>('review');
  const [zoomed, setZoomed] = useState(false);
  const [textScale, setTextScale] = useState(1.0);
  const [activeTabId, setActiveTabId] = useState(MOCK_TASKS[0]?.actionItems[0]?.id || '');
  const [subAction, setSubAction] = useState<SubAction>(null);
  const [inputMethod, setInputMethod] = useState<InputMethod>(null);
  const [inputText, setInputText] = useState('');
  const [listening, setListening] = useState(false);
  const currentTask = tasks[currentIndex];
  const resetNav = () => {
    setFocus('source');
    setMode('review');
    setZoomed(false);
    setSubAction(null);
    setInputMethod(null);
    setInputText('');
    setListening(false);
  };
  const goNext = useCallback(() => {
    setCurrentIndex(prev => Math.min(prev + 1, tasks.length - 1));
    resetNav();
  }, [tasks.length]);
  const goPrev = useCallback(() => {
    setCurrentIndex(prev => Math.max(prev - 1, 0));
    resetNav();
  }, []);
  useEffect(() => {
    if (currentTask) setActiveTabId(currentTask.actionItems[0]?.id || '');
  }, [currentTask?.id]);
  const updateTask = useCallback((fn: (t: Task) => Task) => {
    setTasks(prev => prev.map((t, i) => i === currentIndex ? fn(t) : t));
  }, [currentIndex]);

  // Approve: auto-accept any remaining agent suggestions, then send
  const handleApprove = useCallback(() => {
    if (!currentTask) return;
    const canApprove = computeCanApprove(currentTask.actionItems);
    if (!canApprove) return;
    updateTask(t => ({
      ...t,
      state: 'approved' as const,
      actionItems: t.actionItems.map(ai => ai.status === 'needs_input' && ai.agentProvided ? {
        ...ai,
        status: 'resolved' as const,
        userInput: ai.agentProvided
      } : ai)
    }));
    setTimeout(goNext, 500);
  }, [currentTask, updateTask, goNext]);

  // Delegated close: all items delegated, task enters waiting
  const handleDelegatedClose = useCallback(() => {
    updateTask(t => ({
      ...t,
      state: 'delegated_waiting' as const
    }));
    setTimeout(goNext, 500);
  }, [updateTask, goNext]);
  const handleAccept = useCallback((itemId: string) => {
    updateTask(t => {
      const item = t.actionItems.find(a => a.id === itemId);
      if (!item?.agentProvided) return t;
      return {
        ...t,
        actionItems: t.actionItems.map(a => a.id === itemId ? {
          ...a,
          status: 'resolved' as const,
          userInput: a.agentProvided
        } : a)
      };
    });
  }, [updateTask]);
  const handleSaveAmend = useCallback((itemId: string, text: string) => {
    if (!text) return;
    updateTask(t => {
      const item = t.actionItems.find(a => a.id === itemId);
      const val = item?.agentProvided ? `${item.agentProvided} [amended: ${text}]` : text;
      return {
        ...t,
        actionItems: t.actionItems.map(a => a.id === itemId ? {
          ...a,
          status: 'resolved' as const,
          userInput: val
        } : a)
      };
    });
    setSubAction(null);
    setInputMethod(null);
    setInputText('');
    setListening(false);
  }, [updateTask]);
  const handleDelegateConfirm = useCallback((itemId: string, type: string, instruction: string) => {
    updateTask(t => ({
      ...t,
      actionItems: t.actionItems.map(a => a.id === itemId ? {
        ...a,
        status: 'delegated' as const,
        userInput: `[${type}] ${instruction || 'No instructions'}`
      } : a)
    }));
    setSubAction(null);
    setInputMethod(null);
    setInputText('');
    setListening(false);
  }, [updateTask]);

  // Gamepad
  const jd = useRef(0);
  useEffect(() => {
    if (!gamepad.connected || isMobile) return;
    const n = Date.now();
    if (n - jd.current < 200) return;
    if (gamepad.leftX > 0.5) {
      setFocus('response');
      jd.current = n;
    }
    ;
    if (gamepad.leftX < -0.5) {
      setFocus('source');
      jd.current = n;
    }
  }, [gamepad.leftX, gamepad.connected, isMobile]);
  useButtonEdge(gamepad.a, useCallback(() => {
    if (isMobile || !currentTask) return;
    const ca = computeCanApprove(currentTask.actionItems);
    const ad = computeAllDelegated(currentTask.actionItems);
    if (mode === 'review') {
      if (ad) handleDelegatedClose();else if (ca) handleApprove();
    }
  }, [mode, currentTask, handleApprove, handleDelegatedClose, isMobile]));
  useButtonEdge(gamepad.x, useCallback(() => {
    if (isMobile) return;
    if (mode === 'review') {
      setMode('refine');
      setActiveTabId(currentTask?.actionItems[0]?.id || '');
    } else {
      setMode('review');
      setSubAction(null);
    }
  }, [mode, currentTask, isMobile]));
  useButtonEdge(gamepad.b, useCallback(() => {
    if (isMobile) return;
    if (subAction) {
      setSubAction(null);
      setInputMethod(null);
      setInputText('');
    } else if (mode === 'refine') setMode('review');else if (zoomed) setZoomed(false);
  }, [mode, zoomed, subAction, isMobile]));
  useButtonEdge(gamepad.l1, useCallback(() => setTextScale(p => Math.max(0.7, p - 0.1)), []));
  useButtonEdge(gamepad.r1, useCallback(() => setTextScale(p => Math.min(1.6, p + 0.1)), []));
  useButtonEdge(gamepad.l2 > 0.5, useCallback(() => goPrev(), [goPrev]));
  useButtonEdge(gamepad.r2 > 0.5, useCallback(() => goNext(), [goNext]));
  if (!currentTask) return <div className="min-h-[calc(100vh-56px)] w-full flex items-center justify-center px-6" data-id="element-2260"><EmptyState message="All caught up." data-id="element-2261" /></div>;
  const props = {
    task: currentTask,
    tasks,
    currentIndex,
    mode,
    focus,
    zoomed,
    textScale,
    activeTabId,
    subAction,
    inputMethod,
    inputText,
    listening,
    setFocus,
    setZoomed,
    setMode,
    setActiveTabId,
    setSubAction,
    setInputMethod,
    setInputText,
    setListening,
    handleApprove,
    handleAccept,
    handleSaveAmend,
    handleDelegateConfirm,
    handleDelegatedClose,
    goNext,
    goPrev,
    gamepad
  };
  return isMobile ? <MobileLayout {...props} data-id="element-2262" /> : <DesktopLayout {...props} data-id="element-2263" />;
}